import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  TextField,
  Divider,
  Box,
  IconButton,
  Typography,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem,
  SvgIcon,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import { useAtom } from "jotai";
import { themeAtom, isMobileAtom } from "../-Atoms";
import EmailField from "./EmailField";
import ScheduleEmailModal from "./ScheduleEmailModal";
import { saveEmailAddresses } from "../Utils/saveEmailAddresses";

const EmailModalV2 = ({
  open,
  onClose,
  initialTo = [],
  initialCc = [],
  initialBcc = [],
  template = "",
  subject: initialSubject = "",
  onSend = null,
  customButtons = null, // For custom toolbar buttons like Select Dates
  fromEmail = "production@imagesthatsell.com.au", // Default from email
  jobActions = null, // Actions to perform on jobs after email is sent
  onSubjectChange = null, // Callback when subject changes
}) => {
  const [theme] = useAtom(themeAtom);
  const [isMobile] = useAtom(isMobileAtom);

  const [toEmails, setToEmails] = useState(initialTo);
  const [ccEmails, setCcEmails] = useState(initialCc);
  const [bccEmails, setBccEmails] = useState(initialBcc);
  const [replyToEmails, setReplyToEmails] = useState([]);
  const [subject, setSubject] = useState(initialSubject);
  const [emailHTML, setEmailHTML] = useState(template);

  const [ccVisible, setCcVisible] = useState(true);
  const [bccVisible, setBccVisible] = useState(false);
  const [replyToVisible, setReplyToVisible] = useState(false);
  const [writingOptionsVisible, setWritingOptionsVisible] = useState(true);

  // Track if email fields have been initialized to prevent continuous syncing
  const [isInitialized, setIsInitialized] = useState(false);

  const [fieldsMenuAnchor, setFieldsMenuAnchor] = useState(null);
  const [savedSelection, setSavedSelection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);

  // Scheduling state
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  const emailContentRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.shiftKey && e.key === "=") {
          e.preventDefault();
          increaseFontSize();
          return;
        }
        if (e.shiftKey && e.key === "-") {
          e.preventDefault();
          decreaseFontSize();
          return;
        }

        switch (e.key.toLowerCase()) {
          case "b":
            e.preventDefault();
            execCommand("bold");
            break;
          case "i":
            e.preventDefault();
            execCommand("italic");
            break;
          case "u":
            e.preventDefault();
            execCommand("underline");
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addAttachments(files);
  };

  // Add attachments with validation
  const addAttachments = (files) => {
    const maxSize = 25 * 1024 * 1024; // 25MB in bytes
    const validFiles = [];

    for (let file of files) {
      if (file.size > maxSize) {
        alert(
          `File ${file.name} exceeds the 25MB limit and will not be added.`
        );
        continue;
      }
      validFiles.push(file);
    }

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      dragCounter.current++;
      setDragActive(true);
    } else if (e.type === "dragleave") {
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setDragActive(false);
      }
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      addAttachments(Array.from(e.dataTransfer.files));
    }
  };

  useEffect(() => {
    // Initialize email fields only when modal opens and hasn't been initialized yet
    if (open && !isInitialized) {
      setToEmails(initialTo || []);
      setCcEmails(initialCc || []);
      setBccEmails(initialBcc || []);
      setReplyToEmails([]);
      setAttachments([]);
      if (initialBcc && initialBcc.length > 0) setBccVisible(true);
      setIsInitialized(true);
    }

    // Reset initialization flag when modal closes
    if (!open) {
      setIsInitialized(false);
      setAttachments([]);
    }
  }, [open, initialTo, initialCc, initialBcc, isInitialized]);

  useEffect(() => {
    setSubject(initialSubject);
  }, [initialSubject]);

  useEffect(() => {
    setEmailHTML(template);
  }, [template]);

  // // Make links and buttons clickable (not editable)
  useEffect(() => {
    if (emailContentRef.current) {
      const links = emailContentRef.current.querySelectorAll("a, button");
      links.forEach((link) => {
        link.setAttribute("contenteditable", "false");
        link.style.cursor = "pointer";
        link.addEventListener("click", (e) => {
          e.stopPropagation();
          // Open links in new tab
          if (link.tagName === "A" && link.href) {
            window.open(link.href, "_blank");
          }
        });
      });
    }
  }, [emailHTML]);

  useEffect(() => {
    const detectZoom = () => {
      const baseWidth = 1920;
      const currentWidth = window.innerWidth;
      const estimatedZoom = currentWidth / baseWidth;
      setZoomScale(estimatedZoom);
    };

    detectZoom();

    const handleResize = () => {
      setTimeout(detectZoom, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSend = async () => {
    setIsLoading(true);

    try {
      const currentHTML = emailContentRef.current?.innerHTML || emailHTML;

      // Convert file attachments to base64 for email sending
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                filename: file.name,
                content: e.target.result.split(",")[1], // Remove data URL prefix
                encoding: "base64",
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      const emailData = {
        to: toEmails.map((email) => email.email || email),
        cc: ccEmails.map((email) => email.email || email),
        bcc: bccEmails.map((email) => email.email || email),
        replyTo: replyToEmails.map((email) => email.email || email),
        subject,
        htmlContent: currentHTML,
        plainTextContent: emailContentRef.current?.innerText || "",
        attachments: attachmentData,
        fromEmail: fromEmail,
      };

      if (onSend) {
        await saveEmailAddresses(toEmails, ccEmails, bccEmails);
        await onSend(emailData);
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error sending email:", error);
      // Don't close modal on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleScheduleEmail = async (scheduledTime) => {
    setIsScheduling(true);

    try {
      const currentHTML = emailContentRef.current?.innerHTML || emailHTML;

      // Convert file attachments to base64 for email sending
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                filename: file.name,
                content: e.target.result.split(",")[1], // Remove data URL prefix
                encoding: "base64",
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      const emailData = {
        to: toEmails.map((email) => email.email || email),
        cc: ccEmails.map((email) => email.email || email),
        bcc: bccEmails.map((email) => email.email || email),
        replyTo: replyToEmails.map((email) => email.email || email),
        subject,
        htmlContent: currentHTML,
        plainTextContent: emailContentRef.current?.innerText || "",
        fromEmail: fromEmail,
        attachments: attachmentData,
      };

      // Import Firebase functions
      const { httpsCallable } = await import("firebase/functions");
      const { functions } = await import("../firebaseConfig");

      const scheduleEmail = httpsCallable(functions, "scheduleEmail");

      await scheduleEmail({
        scheduledTime,
        emailData,
        jobActions,
      });

      console.log("📧 Email scheduled for:", scheduledTime);
      setScheduleModalOpen(false);
      onClose();
    } catch (error) {
      console.error("Error scheduling email:", error);
      // Don't close modal on error
    } finally {
      setIsScheduling(false);
    }
  };

  const toggleCC = () => setCcVisible(!ccVisible);
  const toggleBCC = () => setBccVisible(!bccVisible);
  const toggleReplyTo = () => setReplyToVisible(!replyToVisible);

  const handleFieldsMenuClick = (event) => {
    setFieldsMenuAnchor(event.currentTarget);
  };

  const handleFieldsMenuClose = () => {
    setFieldsMenuAnchor(null);
  };

  const execCommand = (command, value = null) => {
    emailContentRef.current?.focus();
    const success = document.execCommand(command, false, value);
    console.log(`Command ${command} executed:`, success);

    return success;
  };

  const handleColorChange = (color) => {
    emailContentRef.current?.focus();

    if (savedSelection) {
      try {
        restoreSelection();
        document.execCommand("styleWithCSS", false, true);
        document.execCommand("foreColor", false, color.hex);
      } catch (error) {
        console.error("Color application error:", error);
      }
      setSavedSelection(null);
    } else {
      execCommand("styleWithCSS", true);
      execCommand("foreColor", color.hex);
    }
  };

  const colors = [
    "#000000",
    "#FFFFFF",
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FF6600",
    "#800080",
    "#00CED1",
    "#32CD32",
    "#FF1493",
    "#8A2BE2",
    "#FF4500",
    "#2E8B57",
    "#DC143C",
    "#4169E1",
    "#228B22",
    "#FF8C00",
    "#9932CC",
    "#B22222",
    "#008B8B",
  ];

  const isDark = theme === "dark";

  const themeStyles = {
    background: isDark ? "#1a1a1a" : "#f3f4f6",
    color: isDark ? "#ffffff" : "#000000",
    borderColor: isDark ? "#2c2c2e" : "#d1d1d6",
    headerBg: isDark ? "#1c1c1e" : "#f2f2f7",
    toolbarBg: isDark ? "#2c2c2e" : "#f2f2f7",
    buttonBg: isDark ? "#3a3a3c" : "#ffffff",
    shadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      setSavedSelection(range.cloneRange());
      console.log("Saved selection:", range.toString());
      return true;
    }
    setSavedSelection(null);
    return false;
  };

  const restoreSelection = () => {
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
      return true;
    }
    return false;
  };

  const increaseFontSize = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      try {
        const range = selection.getRangeAt(0);
        const span = document.createElement("span");

        const parentElement =
          range.commonAncestorContainer.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentElement
            : range.commonAncestorContainer;
        const currentSize =
          parseInt(window.getComputedStyle(parentElement).fontSize) || 14;
        const newSize = Math.min(currentSize + 2, 32);

        span.style.fontSize = `${newSize}px`;

        const selectedContent = range.extractContents();
        span.appendChild(selectedContent);
        range.insertNode(span);

        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        console.error("Font size increase error:", e);
      }
    }
  };

  const decreaseFontSize = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      try {
        const range = selection.getRangeAt(0);
        const span = document.createElement("span");

        const parentElement =
          range.commonAncestorContainer.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentElement
            : range.commonAncestorContainer;
        const currentSize =
          parseInt(window.getComputedStyle(parentElement).fontSize) || 14;
        const newSize = Math.max(currentSize - 2, 8);

        span.style.fontSize = `${newSize}px`;

        const selectedContent = range.extractContents();
        span.appendChild(selectedContent);
        range.insertNode(span);

        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        console.error("Font size decrease error:", e);
      }
    }
  };

  const calculateScaleFactor = () => {
    return Math.max(1, Math.min(1.4, zoomScale));
  };

  const scaleFactor = calculateScaleFactor();
  const dynamicPadding = `${(scaleFactor - 1) * 30}%`;

  const getDialogMaxWidth = () => {
    if (isMobile) return false;
    if (zoomScale > 1.2) return "xl";
    return "lg";
  };

  const SendIcon = ({ fontSize = "small", ...props }) => {
    const sizeMap = {
      small: { width: "1rem", height: "1rem" },
      medium: { width: "1.25rem", height: "1.25rem" },
      large: { width: "1.75rem", height: "1.75rem" },
    };

    const iconSize = sizeMap[fontSize] || sizeMap.small;

    return (
      <SvgIcon
        {...props}
        viewBox="0 0 19.2676 23.6816"
        sx={{
          width: iconSize.width,
          height: iconSize.height,
          ...props.sx,
        }}
      >
        <path
          d="M0.917969 10.3906C1.17188 10.3906 1.42578 10.3027 1.5918 10.1367L5.05859 6.70898L9.44336 1.94336L13.8477 6.70898L17.3145 10.1367C17.4805 10.3027 17.7344 10.3906 17.9883 10.3906C18.5156 10.3906 18.9062 9.99023 18.9062 9.45312C18.9062 9.19922 18.8184 8.98438 18.6133 8.76953L10.166 0.322266C9.96094 0.107422 9.7168 0 9.45312 0C9.18945 0 8.94531 0.107422 8.74023 0.322266L0.292969 8.76953C0.0878906 8.98438 0 9.19922 0 9.45312C0 9.99023 0.390625 10.3906 0.917969 10.3906ZM9.45312 23.6816C10.0098 23.6816 10.4004 23.291 10.4004 22.7441L10.4004 5.56641L10.293 2.03125C10.293 1.5332 9.95117 1.18164 9.45312 1.18164C8.95508 1.18164 8.61328 1.5332 8.61328 2.02148L8.50586 5.56641L8.50586 22.7441C8.50586 23.291 8.89648 23.6816 9.45312 23.6816Z"
          fill="white"
          fillOpacity="0.85"
        />
      </SvgIcon>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? null : onClose}
      maxWidth={getDialogMaxWidth()}
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            height: isMobile ? "100vh" : "95vh",
            maxHeight: isMobile ? "100vh" : "95vh",
            borderRadius: isMobile ? 0 : "20px",
            bgcolor: themeStyles.background,
            color: themeStyles.color,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        },
      }}
      // Add drag and drop event handlers to the entire dialog
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
    >
      <Toolbar
        variant="dense"
        sx={{
          borderBottom: `1px solid ${themeStyles.borderColor}`,
          bgcolor: themeStyles.headerBg,
          justifyContent: "space-between",
          minHeight: 52,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          position: "relative",
          p: "12px !important",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          size="medium"
          sx={{
            color: isLoading
              ? isDark
                ? "rgba(255,255,255,0.3)"
                : "rgba(0,0,0,0.3)"
              : themeStyles.color,
            borderRadius: "50%",
            "&:hover": {
              bgcolor: isLoading
                ? "transparent"
                : isDark
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
            },
            "&:disabled": {
              color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              cursor: "not-allowed",
            },
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Tooltip title="Writing Options">
            <IconButton
              size="medium"
              onClick={() => setWritingOptionsVisible(!writingOptionsVisible)}
              sx={{
                color: writingOptionsVisible ? "#007AFF" : themeStyles.color,
                bgcolor: writingOptionsVisible
                  ? isDark
                    ? "rgba(0,122,255,0.2)"
                    : "rgba(0,122,255,0.1)"
                  : "transparent",
                borderRadius: "50%",
                "&:hover": {
                  bgcolor: writingOptionsVisible
                    ? isDark
                      ? "rgba(0,122,255,0.3)"
                      : "rgba(0,122,255,0.15)"
                    : isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                },
              }}
            >
              <EditIcon fontSize="medium" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Attachments">
            <IconButton
              size="medium"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                color: attachments.length > 0 ? "#34C759" : themeStyles.color,
                borderRadius: "50%",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                },
              }}
            >
              <AttachFileIcon fontSize="medium" />
            </IconButton>
          </Tooltip>

          {customButtons && customButtons}

          <Tooltip title="Options">
            <IconButton
              size="medium"
              onClick={handleFieldsMenuClick}
              sx={{
                color: themeStyles.color,
                borderRadius: "50%",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                },
              }}
            >
              <MoreHorizIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Schedule Email">
            <IconButton
              onClick={() => setScheduleModalOpen(true)}
              disabled={
                !toEmails.length || !subject.trim() || isLoading || isScheduling
              }
              sx={{
                bgcolor: "#FF9500",
                color: "white",
                borderRadius: "50%",
                width: "2.25rem",
                height: "2.25rem",
                "&:hover": {
                  bgcolor: "#CC7700",
                  transform: "scale(1.02)",
                },
                "&:disabled": {
                  bgcolor: isDark ? "#3a3a3c" : "#e5e5ea",
                  color: isDark ? "#6d6d70" : "#c7c7cc",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ScheduleIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={handleSend}
            disabled={
              !toEmails.length || !subject.trim() || isLoading || isScheduling
            }
            sx={{
              bgcolor: "#007AFF",
              color: "white",
              borderRadius: "50%",
              width: "2.25rem",
              height: "2.25rem",
              "&:hover": {
                bgcolor: "#0056CC",
                transform: "scale(1.02)",
              },
              "&:disabled": {
                bgcolor: isDark ? "#3a3a3c" : "#e5e5ea",
                color: isDark ? "#6d6d70" : "#c7c7cc",
              },
              transition: "all 0.2s ease",
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Toolbar>

      <Menu
        anchorEl={fieldsMenuAnchor}
        open={Boolean(fieldsMenuAnchor)}
        onClose={handleFieldsMenuClose}
        PaperProps={{
          sx: {
            bgcolor: themeStyles.background,
            color: themeStyles.color,
            border: `1px solid ${themeStyles.borderColor}`,
            borderRadius: "16px",
            boxShadow: isDark
              ? "0 10px 30px rgba(0,0,0,0.5)"
              : "0 10px 30px rgba(0,0,0,0.15)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            toggleCC();
            handleFieldsMenuClose();
          }}
          sx={{
            minHeight: 28,
            fontSize: "13px",
            gap: 1,
            py: 0.5,
          }}
        >
          {ccVisible ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
          {ccVisible ? "Hide CC" : "Show CC"}
        </MenuItem>
        <Divider sx={{ my: 0 }} />
        <MenuItem
          onClick={() => {
            toggleBCC();
            handleFieldsMenuClose();
          }}
          sx={{
            minHeight: 28,
            fontSize: "13px",
            gap: 1,
            py: 0.5,
          }}
        >
          {bccVisible ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
          {bccVisible ? "Hide BCC" : "Show BCC"}
        </MenuItem>
        <Divider sx={{ my: 0 }} />
        <MenuItem
          onClick={() => {
            toggleReplyTo();
            handleFieldsMenuClose();
          }}
          sx={{
            minHeight: 28,
            fontSize: "13px",
            gap: 1,
            py: 0.5,
          }}
        >
          {replyToVisible ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
          {replyToVisible ? "Hide Reply-To" : "Show Reply-To"}
        </MenuItem>
      </Menu>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          bgcolor: themeStyles.background,
          overflow: "hidden",
          position: "relative",
        }}
        // Add drag and drop event handlers to the main container as well
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        {/* Hidden file input - always available */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          style={{ display: "none" }}
        />

        {/* Drag overlay - shown when dragging files over the modal */}
        {dragActive && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? "rgba(52, 199, 89, 0.1)" : "rgba(52, 199, 89, 0.05)",
              border: "2px dashed #34C759",
              borderRadius: "8px",
              zIndex: 9998,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                color: themeStyles.color,
                fontWeight: 600,
                textAlign: "center",
                px: 2
              }}
            >
            </Typography>
          </Box>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark
                ? "rgba(30, 30, 30, 0.7)"
                : "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "all",
              cursor: "not-allowed",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={40}
                sx={{
                  color: "#007AFF",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: themeStyles.color,
                  fontWeight: 500,
                }}
              >
                Sending Email...
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            py: 0,
            pl: 3,
            pr: 2,
            borderBottom: `1px solid ${themeStyles.borderColor}`,
            bgcolor: themeStyles.headerBg,
            flexShrink: 0,
            opacity: isLoading ? 0.6 : 1,
            pointerEvents: isLoading ? "none" : "auto",
          }}
        >
          <EmailField
            emails={toEmails}
            setEmails={setToEmails}
            kind="To"
            visible={true}
            marginRight={20}
            disabled={isLoading}
          />

          <Divider sx={{ my: 0 }} />

          <EmailField
            emails={ccEmails}
            setEmails={setCcEmails}
            kind="Cc"
            visible={ccVisible}
            marginRight={0}
            disabled={isLoading}
          />

          {ccVisible && <Divider sx={{ my: 0 }} />}

          <EmailField
            emails={bccEmails}
            setEmails={setBccEmails}
            kind="Bcc"
            visible={bccVisible}
            marginRight={0}
            disabled={isLoading}
          />

          {bccVisible && <Divider sx={{ my: 0 }} />}

          <EmailField
            emails={replyToEmails}
            setEmails={setReplyToEmails}
            kind="Reply-To"
            visible={replyToVisible}
            marginRight={0}
            disabled={isLoading}
          />

          {replyToVisible && <Divider sx={{ my: 0 }} />}

          <Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mr: 2,
                minWidth: "60px",
                fontWeight: 500,
                color: themeStyles.color,
              }}
            >
              Subject:
            </Typography>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              disabled={isLoading}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                onSubjectChange?.(e.target.value);
              }}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "14px",
                  fontWeight: 500,
                  color: themeStyles.color,
                },
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  height: "20px",
                  fontWeight: 500,
                  color: themeStyles.color,
                },
                "& .Mui-disabled": {
                  color: `${themeStyles.color} !important`,
                  "-webkit-text-fill-color": `${themeStyles.color} !important`,
                },
              }}
            />
          </Box>
        </Box>

        {writingOptionsVisible && (
          <Toolbar
            variant="dense"
            sx={{
              borderBottom: `1px solid ${themeStyles.borderColor}`,
              bgcolor: themeStyles.toolbarBg,
              minHeight: 56,
              p: 0.5,
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              flexShrink: 0,
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: themeStyles.buttonBg,
                  borderRadius: "24px",
                  boxShadow: themeStyles.shadow,
                }}
              >
                <Tooltip title="Bold (⌘B)">
                  <IconButton
                    size="medium"
                    onClick={() => execCommand("bold")}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      color: themeStyles.color,
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic (⌘I)">
                  <IconButton
                    size="medium"
                    onClick={() => execCommand("italic")}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      color: themeStyles.color,
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline (⌘U)">
                  <IconButton
                    size="medium"
                    onClick={() => execCommand("underline")}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      color: themeStyles.color,
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: themeStyles.buttonBg,
                  borderRadius: "24px",
                  p: 1,
                  boxShadow: themeStyles.shadow,
                  flexWrap: "wrap",
                  maxWidth: isMobile ? "100%" : "auto",
                }}
              >
                {colors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleColorChange({ hex: color })}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveSelection();
                    }}
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: color,
                      border: "1px solid #ccc",
                      borderRadius: "45%",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.1)",
                        borderColor: "#999",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Toolbar>
        )}

        {/* Attachments toolbar - automatically shows when files are added */}
        {(attachments.length > 0) && (
          <Box
            sx={{
              borderBottom: `1px solid ${themeStyles.borderColor}`,
              bgcolor: themeStyles.toolbarBg,
              p: 2,
              flexShrink: 0,
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {attachments.map((file, index) => (
                  <Chip
                    key={index}
                    label={`${file.name} (${(
                      file.size /
                      (1024 * 1024)
                    ).toFixed(1)} MB)`}
                    onDelete={() => removeAttachment(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{
                      borderRadius: 1,
                      bgcolor: isDark ? "#2C2C2E" : "#FFFFFF",
                      color: themeStyles.color,
                      border: `1px solid ${isDark ? "#3a3a3c" : "#d1d1d6"}`,
                      boxShadow: isDark
                        ? "0 1px 3px rgba(0,0,0,0.3)"
                        : "0 1px 3px rgba(0,0,0,0.1)",
                      "& .MuiChip-deleteIcon": {
                        color: isDark ? "#8E8E93" : "#8E8E93",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            bgcolor: themeStyles.background,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              overflowX: "hidden",
              position: "relative",
              bgcolor: themeStyles.background,
            }}
          >
                      <Box
            ref={emailContentRef}
            contentEditable={!isLoading}
            suppressContentEditableWarning
            dangerouslySetInnerHTML={{ __html: emailHTML }}
            sx={{
              bgcolor: themeStyles.background,
              minHeight: "100%",
              p: 0,
              border: "none",
              outline: "none",
              fontSize: "14px",
              lineHeight: 1.6,
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              color: themeStyles.color,
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? "none" : "auto",
              transform: `scale(${scaleFactor})`,
              transformOrigin: "top center",
              paddingTop: 1,
              paddingBottom: dynamicPadding,
              "&:focus": {
                outline: "none",
              },
              cursor: isLoading ? "not-allowed" : "text",
              "& h1": {
                fontSize: "24px",
                fontWeight: 600,
                margin: "16px 0 8px 0",
                color: themeStyles.color,
              },
              "& h2": {
                fontSize: "20px",
                fontWeight: 600,
                margin: "14px 0 6px 0",
                color: themeStyles.color,
              },
              "& h3": {
                fontSize: "16px",
                fontWeight: 600,
                margin: "12px 0 4px 0",
                color: themeStyles.color,
              },
              "& p": {
                margin: "8px 0",
                color: themeStyles.color,
              },
              "& strong": {
                fontWeight: 600,
              },
              "& a": {
                color: "#007bff",
                textDecoration: "underline",
              },
              "& ul, & ol": {
                paddingLeft: "20px",
                margin: "8px 0",
              },
              "& li": {
                margin: "4px 0",
              },
            }}
            // Add drag and drop event handlers to the email content area
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          />
          </Box>
        </Box>
      </Box>

      {/* Schedule Email Modal */}
      <ScheduleEmailModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSchedule={handleScheduleEmail}
        isLoading={isScheduling}
      />
    </Dialog>
  );
};

export default EmailModalV2;
